import React, { useState, useMemo } from 'react';
import { Filter, ChevronRight, ChevronLeft, Layers, Columns, Sparkles, Smartphone } from 'lucide-react';
import { SPEND_VIEW_MODES, type SpendViewMode } from '../../../App';

interface SpendFilterSidebarProps {
  viewMode: SpendViewMode;
  onViewModeChange: (mode: SpendViewMode) => void;
}

const SpendFilterSidebar: React.FC<SpendFilterSidebarProps> = ({ viewMode, onViewModeChange }) => {
  // Remember user preference for sidebar state across sessions
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('marlo:spendFilterExpanded');
    return saved !== null ? saved === 'true' : false; // Default collapsed
  });
  
  // Persist sidebar state to localStorage
  const handleToggle = (expanded: boolean) => {
    setIsExpanded(expanded);
    localStorage.setItem('marlo:spendFilterExpanded', String(expanded));
  };

  // Move filterOptions inside component to avoid circular dependency initialization issues
  const filterOptions = useMemo(() => [
    { mode: SPEND_VIEW_MODES.COMBINED, label: 'Combined', icon: Layers, description: 'All campaigns together' },
    { mode: SPEND_VIEW_MODES.SIDE_BY_SIDE, label: 'Side-by-Side', icon: Columns, description: 'Compare both campaigns' },
    { mode: SPEND_VIEW_MODES.BUBBLE_AI, label: 'AI Only', icon: Sparkles, description: 'Bubble AI only' },
    { mode: SPEND_VIEW_MODES.BUBBLE_MOBILE, label: 'Mobile Only', icon: Smartphone, description: 'Mobile only' },
  ], []);

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:block">
      <div className={`bg-[var(--bg-elev)] border border-[var(--border-subtle)] rounded-xl shadow-lg transition-all duration-300 ${
        isExpanded ? 'p-4 w-56' : 'p-3 w-14'
      }`}>
        {isExpanded ? (
          <>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-[var(--accent-primary)]" />
                <h3 className="text-sm font-semibold theme-text">Filter View</h3>
              </div>
              <button
                onClick={() => handleToggle(false)}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-muted)] transition-colors"
                aria-label="Collapse filter"
              >
                <ChevronLeft className="w-4 h-4 theme-text-muted" />
              </button>
            </div>
            
            <div className="space-y-2">
              {filterOptions.slice(0, 2).map(({ mode, label, icon: Icon, description }) => (
                <button
                  key={mode}
                  onClick={() => onViewModeChange(mode)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    viewMode === mode
                      ? 'bg-[var(--accent-primary)] text-white shadow-sm'
                      : 'theme-text-light theme-surface hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,_transparent)]'
                  }`}
                  title={description}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left">{label}</span>
                </button>
              ))}
              
              <div className="pt-2 border-t border-[var(--border-subtle)]">
                {filterOptions.slice(2).map(({ mode, label, icon: Icon, description }) => (
                  <button
                    key={mode}
                    onClick={() => onViewModeChange(mode)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      viewMode === mode
                        ? mode === SPEND_VIEW_MODES.BUBBLE_MOBILE
                          ? 'bg-[var(--accent-success)] text-white shadow-sm'
                          : 'bg-[var(--accent-primary)] text-white shadow-sm'
                        : 'theme-text-light theme-surface hover:bg-[color-mix(in_srgb,var(--accent-primary)_8%,_transparent)]'
                    }`}
                    title={description}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 text-left">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => handleToggle(true)}
              className="p-2 rounded-lg hover:bg-[var(--bg-muted)] transition-colors"
              aria-label="Expand filter"
            >
              <ChevronRight className="w-4 h-4 theme-text-muted" />
            </button>
            <div className="flex flex-col gap-2">
              {filterOptions.map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => onViewModeChange(mode)}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === mode
                      ? mode === SPEND_VIEW_MODES.BUBBLE_MOBILE
                        ? 'bg-[var(--accent-success)] text-white'
                        : 'bg-[var(--accent-primary)] text-white'
                      : 'theme-text-muted hover:bg-[var(--bg-muted)]'
                  }`}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendFilterSidebar;

